import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CommentApi } from '../api/CommentApi';
import { AudioApi } from '../api/AudioApi';
import { conversationSpeakerNameToRole, selectedTeacherToSpeakerRole } from '../api/SpeakerApi';
import SpeakerBlueIcon from '../asset/icon-speaker-blue.svg';
import AppBarContext from '../context/AppBarContext';
import { handleErrorMessage } from './ErrorDialog';

export interface TeacherAudioReportContext {
    sentenceAudioId?: number | null;
    sentenceId?: number | null;
    textToSpeechSpeakerId?: string | null;
    isSlowAudio?: boolean | null;
    dialogPromptId?: number | null;
    audioText?: string | null;
    audioRole?: string | null;
}

export function getSelectedTeacherSpeakerRole(): string | null {
    return selectedTeacherToSpeakerRole(AudioApi.selectedCustomSentenceTeacher());
}

export function getConversationSpeakerRole(speakerName: string | null | undefined): string | null {
    return conversationSpeakerNameToRole(speakerName);
}

interface TeacherAudioReportDialogProps {
    context: TeacherAudioReportContext | null | undefined;
    open: boolean;
    onClose: () => void;
}

export const TeacherAudioReportDialog: React.FC<TeacherAudioReportDialogProps> = ({ context, open, onClose }) => {
    const { setErrorMessage } = React.useContext(AppBarContext);
    const history = useHistory();
    const location = useLocation();

    const [reportCategory, setReportCategory] = React.useState('audio');
    const [reportText, setReportText] = React.useState('');
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const audioText = context?.audioText?.trim() || '';
    const canSubmit = reportText.trim().length > 0 && !isSubmitting;

    const handleSubmit = () => {
        if (!canSubmit || !context) return;

        setIsSubmitting(true);
        CommentApi.createTeacherAudioComment({
            id: 0,
            bug_report: reportText.trim(),
            bug_category: reportCategory,
            sentence_audio_id: context.sentenceAudioId ?? null,
            sentence_id: context.sentenceId ?? null,
            text_to_speech_speaker_id: context.textToSpeechSpeakerId ?? null,
            is_slow_audio: context.isSlowAudio ?? null,
            dialog_prompt_id: context.dialogPromptId ?? null,
            audio_text: audioText || null,
            audio_role: context.audioRole ?? null,
        })
            .then(response => {
                if (response.status >= 300) {
                    throw new Error('Unable to submit teacher audio report: ' + response.status);
                }
                setErrorMessage('Thanks - audio report submitted.');
                onClose();
                setReportCategory('audio');
                setReportText('');
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Report Audio Problem</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                            <Stack spacing={0.5}>
                                <Typography variant="body2">Audio: {audioText || 'Unavailable'}</Typography>
                                <Typography variant="body2">Role: {context?.audioRole || 'teacher_audio'}</Typography>
                                {context?.sentenceId && (
                                    <Typography variant="body2">Sentence ID: {context.sentenceId}</Typography>
                                )}
                            </Stack>
                        </Paper>
                        <FormControl fullWidth size="small">
                            <InputLabel id="teacher-audio-report-category-label">Category</InputLabel>
                            <Select
                                labelId="teacher-audio-report-category-label"
                                value={reportCategory}
                                label="Category"
                                onChange={(e) => setReportCategory(String(e.target.value))}
                            >
                                <MenuItem value="audio">Audio issue</MenuItem>
                                <MenuItem value="pronunciation">Pronunciation sounds wrong</MenuItem>
                                <MenuItem value="wrong_voice">Wrong voice</MenuItem>
                                <MenuItem value="wrong_text">Audio says the wrong text</MenuItem>
                                <MenuItem value="cutoff">Cut off or incomplete</MenuItem>
                                <MenuItem value="noise">Noise or distortion</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="What went wrong?"
                            value={reportText}
                            onChange={(e) => setReportText(e.target.value)}
                            multiline
                            minRows={4}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>
    );
};

interface TeacherAudioReportButtonProps {
    context: TeacherAudioReportContext | null | undefined;
    size?: 'small' | 'medium';
    edge?: 'start' | 'end' | false;
}

const TeacherAudioReportButton: React.FC<TeacherAudioReportButtonProps> = ({ context, size = 'small', edge = false }) => {
    const [isOpen, setIsOpen] = React.useState(false);

    if (!context) {
        return null;
    }

    return (
        <>
            <Tooltip title="Report audio problem" arrow>
                <IconButton
                    aria-label="Report audio problem"
                    size={size}
                    edge={edge}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setIsOpen(true);
                    }}
                    sx={{
                        color: '#0062FF',
                        p: size === 'small' ? 0.5 : 1,
                        opacity: 0.85,
                        '&:hover': { opacity: 1 },
                    }}
                >
                    <ReportProblemOutlinedIcon fontSize={size} />
                </IconButton>
            </Tooltip>
            <TeacherAudioReportDialog context={context} open={isOpen} onClose={() => setIsOpen(false)} />
        </>
    );
};

interface TeacherAudioActionsProps {
    context: TeacherAudioReportContext | null | undefined;
    onPlay: () => void;
    speakerSize?: number;
    speakerAriaLabel?: string;
    menuAriaLabel?: string;
    showReportMenu?: boolean;
}

export const TeacherAudioActions: React.FC<TeacherAudioActionsProps> = ({
    context,
    onPlay,
    speakerSize = 36,
    speakerAriaLabel = 'Play audio',
    menuAriaLabel = 'Audio options',
    showReportMenu = true,
}) => {
    const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
    const [isReportOpen, setIsReportOpen] = React.useState(false);
    const [hasInteracted, setHasInteracted] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);

    const isMenuOpen = Boolean(menuAnchorEl);
    const canReportAudio = Boolean(context) && showReportMenu;
    const showMenuButton = canReportAudio && (isHovered || hasInteracted || isMenuOpen);

    const hideMenuButton = () => {
        setHasInteracted(false);
        setIsHovered(false);
    };

    const handlePlay = (event?: React.MouseEvent<HTMLElement>) => {
        event?.preventDefault();
        event?.stopPropagation();
        setHasInteracted(true);
        onPlay();
    };

    const handleMenuPlay = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setMenuAnchorEl(null);
        hideMenuButton();
        onPlay();
    };

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setHasInteracted(true);
        setMenuAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setMenuAnchorEl(null);
        hideMenuButton();
    };

    const handleOpenReport = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setMenuAnchorEl(null);
        hideMenuButton();
        setIsReportOpen(true);
    };

    const handleCloseReport = () => {
        setIsReportOpen(false);
        hideMenuButton();
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
        if (!isMenuOpen && !isReportOpen) {
            setHasInteracted(false);
        }
    };

    return (
        <Stack
            direction="row"
            alignItems="center"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={handleMouseLeave}
            sx={{ flexShrink: 0 }}
        >
            <Tooltip title="Play audio" arrow>
                <IconButton
                    aria-label={speakerAriaLabel}
                    onClick={handlePlay}
                    onContextMenu={canReportAudio ? handleOpenMenu : undefined}
                    sx={{ p: 0.75 }}
                >
                    <img src={SpeakerBlueIcon} alt="" style={{ width: `${speakerSize}px`, height: `${speakerSize}px` }} />
                </IconButton>
            </Tooltip>
            {canReportAudio && (
                <Tooltip title="Audio options" arrow>
                    <IconButton
                        aria-label={menuAriaLabel}
                        size="small"
                        onClick={handleOpenMenu}
                        sx={{
                            color: '#0062FF',
                            p: showMenuButton ? 0.25 : 0,
                            width: showMenuButton ? 24 : 0,
                            minWidth: 0,
                            overflow: 'hidden',
                            opacity: showMenuButton ? 1 : 0,
                            pointerEvents: showMenuButton ? 'auto' : 'none',
                            transition: 'opacity 120ms ease, width 120ms ease, padding 120ms ease',
                        }}
                    >
                        <MoreVertRoundedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            <Menu
                anchorEl={menuAnchorEl}
                open={isMenuOpen}
                onClose={handleCloseMenu}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
            >
                <MenuItem onClick={handleMenuPlay}>
                    <ListItemIcon>
                        <img src={SpeakerBlueIcon} alt="" style={{ width: '20px', height: '20px' }} />
                    </ListItemIcon>
                    <ListItemText>Play audio</ListItemText>
                </MenuItem>
                {canReportAudio && (
                    <MenuItem onClick={handleOpenReport}>
                        <ListItemIcon>
                            <ReportProblemOutlinedIcon fontSize="small" sx={{ color: '#0062FF' }} />
                        </ListItemIcon>
                        <ListItemText>Report audio problem</ListItemText>
                    </MenuItem>
                )}
            </Menu>
            {canReportAudio && (
                <TeacherAudioReportDialog
                    context={context}
                    open={isReportOpen}
                    onClose={handleCloseReport}
                />
            )}
        </Stack>
    );
};

export default TeacherAudioReportButton;
